import type { ProductVariant } from "@workspace/crystallize/generated/discovery/";
import { Button } from "@workspace/ui/components/button";
import { FormattedPrice } from "@workspace/ui/components/formatted-price";
import { cn } from "@workspace/ui/lib/utils";
import { Loader2, ShoppingCart } from "lucide-react";
import { useState } from "react";
import { useCart } from "~/components/cart/cart-provider";
import QuantitySelector from "~/components/quantity-selector";
import CrystallizeResponsiveImage from "./crystallize-responsive-image";

export default function ProductExtrasSelectorV2({
	extras,
}: {
	extras: ProductVariant[];
}) {
	const { updateCart, isLoading } = useCart();
	const [quantities, setQuantities] = useState<Record<string, number>>({});
	const [addingSkus, setAddingSkus] = useState<Set<string>>(new Set());

	function hasSkuAndPrice(
		extra: ProductVariant,
	): extra is ProductVariant & { sku: string; defaultPrice: number } {
		return (
			typeof extra?.sku === "string" && typeof extra?.defaultPrice === "number"
		);
	}

	const extrasWithSkuAndPrice = extras.filter(hasSkuAndPrice);

	const handleQuantityChange = (sku: string, quantity: number) => {
		setQuantities((prev) => ({ ...prev, [sku]: quantity }));
	};

	const handleAddToCart = (extra: ProductVariant & { sku: string }) => {
		const quantity = quantities[extra.sku] ?? 1;
		setAddingSkus((prev) => new Set(prev).add(extra.sku));

		updateCart(extra.sku, quantity, "update", true);

		// Clear the loading state after a short delay
		setTimeout(() => {
			setAddingSkus((prev) => {
				const next = new Set(prev);
				next.delete(extra.sku);
				return next;
			});
		}, 500);
	};

	return (
		<div className="grid grid-cols-1 gap-4">
			{extrasWithSkuAndPrice.map((extra) => {
				const isAdding = addingSkus.has(extra.sku);
				const quantity = quantities[extra.sku] ?? 1;

				return (
					<div
						key={extra.sku}
						className={cn(
							"grid items-center gap-3 rounded-lg border-2 p-3 transition-colors",
							"grid-cols-[4.5rem_1fr_auto]",
							"border-border",
						)}
					>
						{/* Image */}
						{extra.images?.[0] && (
							<div className="aspect-square h-18 w-18 overflow-hidden rounded-md bg-muted">
								<CrystallizeResponsiveImage
									image={extra.images[0]}
									className="block h-full w-full object-cover"
									sizes="72px"
								/>
							</div>
						)}

						{/* Text and controls */}
						<div className="min-w-0 space-y-2">
							<div>
								<span className="block truncate font-medium md:whitespace-normal">
									{extra.name}
								</span>
								{extra.product?.brief && (
									<p className="text-muted-foreground text-sm">
										{extra.product.brief}
									</p>
								)}
							</div>

							{/* Quantity selector and add to cart button */}
							<div className="flex flex-wrap items-center justify-between gap-2">
								<QuantitySelector
									defaultValue={1}
									minValue={1}
									maxValue={extra.maximumQuantity ?? 100}
									onQuantityChange={(value) =>
										handleQuantityChange(extra.sku, value)
									}
								/>
								<Button
									size="sm"
									onClick={() => handleAddToCart(extra)}
									disabled={isLoading || isAdding}
									className="gap-2"
								>
									{isAdding ? (
										<Loader2 className="h-4 w-4 animate-spin" />
									) : (
										<ShoppingCart className="h-4 w-4" />
									)}
									<span className="hidden sm:inline">Legg til</span>
								</Button>
							</div>
						</div>

						{/* Price */}
						<div className="flex flex-col items-end gap-1">
							<FormattedPrice
								className="font-medium text-lg"
								price={extra.defaultPrice}
							/>
							{quantity > 1 && (
								<span className="text-muted-foreground text-xs">
									= <FormattedPrice price={extra.defaultPrice * quantity} />
								</span>
							)}
						</div>
					</div>
				);
			})}
		</div>
	);
}
